import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Stack,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useNavigate, useParams } from "react-router-dom";
import DeliveryPincodesDialog from "./DeliveryPincodesDialog";
import AddEditBin from "../../components/settings/AddEditBin";
import { getBinsByRackId, deleteBin, getRacksByRoomId } from "../../services/WarehouseSettings";

const BinsPage = () => {
  const navigate = useNavigate();
  const { buildingId, floorId, roomId, rackId } = useParams();
  const [bins, setBins] = useState<any[]>([]);
  const [rack, setRack] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [prefilledRow, setPrefilledRow] = useState<number | undefined>();
  const [prefilledCol, setPrefilledCol] = useState<number | undefined>();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [pincodesDialogOpen, setPincodesDialogOpen] = useState(false);

  const loadRack = useCallback(async () => {
    try {
      if (!roomId) return;
      const data = await getRacksByRoomId(Number(roomId));
      const foundRack = Array.isArray(data) ? data.find((r: any) => r.id === Number(rackId)) : null;
      setRack(foundRack || null);
    } catch (err) {
      console.error("Load rack failed:", err);
      setSnackbar({ open: true, message: "Failed to load rack details", severity: "error" });
    }
  }, [roomId, rackId]);

  const loadBins = useCallback(async () => {
    try {
      if (!rackId) return;
      setLoading(true);
      const data = await getBinsByRackId(Number(rackId));
      setBins(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Load bins failed:", err);
      setBins([]);
      setSnackbar({ open: true, message: "Failed to load bins", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [rackId]);

  const saveDeliveryPincodes = async (pincodes: string[]) => {
    try {
      console.log("Saved pincodes:", pincodes);
      setSnackbar({ open: true, message: "Delivery pincodes saved successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to save delivery pincodes", severity: "error" });
    }
  };

  useEffect(() => {
    if (rackId && roomId) {
      loadRack();
      loadBins();
    }
  }, [rackId, roomId, loadRack, loadBins]);

  const openAddBin = (row?: number, col?: number) => {
    setEditData(null);
    setPrefilledRow(row);
    setPrefilledCol(col);
    setDialogOpen(true);
  };

  const openEditBin = (bin: any) => {
    setEditData(bin);
    setPrefilledRow(undefined);
    setPrefilledCol(undefined);
    setDialogOpen(true);
  };

  const handleSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    loadBins();
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteBin(id);
      loadBins();
      setSnackbar({ open: true, message: "Bin deleted successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete bin", severity: "error" });
    }
  };

  const matrixRows = rack?.rows ? Array.from({ length: rack.rows }, (_, i) => i + 1) : [];
  const maxCols = rack?.coloumns || 0;
  const binsByRow = matrixRows.reduce((acc: any, row: number) => {
    acc[row] = bins.filter((b) => Number(b.row) === Number(row)).sort((a, b) => a.coloumn - b.coloumn);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          onClick={() => navigate(`/settings/warehouse/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}/racks`)}
          sx={{ border: "none", textTransform: "none", color: "text.primary" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => setPincodesDialogOpen(true)}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "transparent",
              borderColor: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
          endIcon={<SettingsOutlinedIcon sx={{ color: "primary.main" }} />}
        >
          Delivery Pin codes
        </Button>
      </Box>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">All Bins - {bins.length}</Typography>
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => openAddBin()}
            sx={{ borderRadius: "6px", textTransform: "none" }}
          >
            Add Bin
          </Button>
        </Box>

        <Box sx={{ flexShrink: 0, mb: 3 }}>
          <Typography fontWeight={600} mb={1}>
            Rack details
          </Typography>
          <Box sx={{ backgroundColor: "background.default", borderRadius: 2, p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography fontWeight={600} fontSize={16}>
                  {rack?.id || "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rack ID
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.300" }} />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography fontWeight={600} fontSize={16}>
                  {rack?.rackName || "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rack Name
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.300" }} />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography fontWeight={600} fontSize={16}>
                  {bins.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Bins
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.300" }} />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography fontWeight={600} fontSize={16}>
                  {rack?.maxLoad || 0} KG
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max Load
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.300" }} />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography fontWeight={600} fontSize={16}>
                  {rack?.availablePercentage}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Availability
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Typography fontWeight={600} mb={1}>
            Bins
          </Typography>
          <Box sx={{ borderRadius: 2, backgroundColor: "background.default", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                <CircularProgress />
              </Box>
            ) : (
            <Box sx={{ px: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `80px repeat(${maxCols}, minmax(120px, 1fr))`,
                gap: 1,
                mb: 2,
                fontWeight: 600,
                color: "grey.500",
              }}
            >
              <Box />
              {Array.from({ length: maxCols }).map((_, i) => (
                <Box key={i} sx={{ textAlign: "center" }}>
                  Column {i + 1}
                </Box>
              ))}
            </Box>

            {matrixRows.map((row) => (
              <Box key={row} sx={{ display: "grid", gridTemplateColumns: `80px 1fr`, alignItems: "center", mb: 1.5 }}>
                <Typography fontWeight={600} color="grey.500">
                  L{row}
                </Typography>
                <Box
                  sx={{
                    border: "2.5px solid",
                    borderColor: "grey.primary",
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.5,
                    background: "background.paper",
                    display: "grid",
                    gridTemplateColumns: `repeat(${maxCols}, minmax(120px, 1fr))`,
                    gap: 1,
                  }}
                >
                  {Array.from({ length: maxCols }).map((_, colIndex) => {
                    const bin = binsByRow[row]?.find((b: any) => b.coloumn === colIndex + 1);
                    return bin ? (
                      <Box key={bin.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            flex: 1,
                            height: 44,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: "grey.300",
                            position: "relative",
                            overflow: "hidden",
                            backgroundColor: "primary.light",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${bin.availablePercentage === 0.0 && bin.usedPercentage >= 100 ? bin.usedPercentage : bin.availablePercentage}%`,
                              backgroundColor: bin.availablePercentage === 0.0 && bin.usedPercentage >= 100 ? "error.main" : bin.availablePercentage <= 30 ? "warning.main" : "success.main"
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.primary" }}>
                              C{bin.coloumn}L{bin.row}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => openEditBin(bin)} 
                          sx={{ 
                            color: "primary.main",
                            "&:hover": { backgroundColor: "primary.light" }
                          }}
                        >
                          <Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1rem", height: "1rem" }} />
                        </IconButton>
                        {bin.canDelete && (
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(bin.id)} 
                            sx={{ 
                              color: "error.main",
                              "&:hover": { backgroundColor: "error.light" }
                            }}
                          >
                            <Box component="img" src="/delete.svg" alt="Delete" sx={{ width: "1rem", height: "1rem" }} />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box key={colIndex} sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          onClick={() => openAddBin(row, colIndex + 1)}
                          sx={{
                            flex: 1,
                            height: 44,
                            borderRadius: 2,
                            border: "2px dashed",
                            borderColor: "grey.300",
                            backgroundColor: "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "grey.100" },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            C{colIndex + 1}L{row}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
            </Box>
            )}

            <Divider sx={{ mx: 2 }} />
            
            <Stack direction="row" spacing={4} justifyContent="center" sx={{ p: 2, pb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "success.main" }} />
                <Typography variant="caption">Available</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "warning.main" }} />
                <Typography variant="caption">Mostly Occupied</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "error.main" }} />
                <Typography variant="caption">Filled</Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Paper>

      <AddEditBin
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editData={editData}
        buildingId={buildingId!}
        floorId={floorId!}
        roomId={roomId!}
        rackId={rackId!}
        prefilledRow={prefilledRow}
        prefilledCol={prefilledCol}
        onSuccess={handleSuccess}
      />

      <DeliveryPincodesDialog
        open={pincodesDialogOpen}
        onClose={() => setPincodesDialogOpen(false)}
        onSave={saveDeliveryPincodes}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default BinsPage;
